/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * BottomNavSection — single section tab icon button.
 *
 * Shows Lucide icon (24px) + short label (10px) stacked vertically.
 * Active section highlighted with module accent color.
 * Badge dot when any child item has alerts.
 */

import { useTranslation } from 'react-i18next';
import type { BottomNavSection as SectionType } from './types';

interface Props {
  section: SectionType;
  isActive: boolean;
  isExpanded: boolean;
  isFadedOut: boolean;
  onTap: () => void;
}

export function BottomNavSectionTab({ section, isActive, isExpanded, isFadedOut, onTap }: Props) {
  const { t } = useTranslation('common');

  const Icon = section.icon ?? section.items[0]?.icon;
  const totalBadges = section.items.reduce((sum, item) => sum + (item.badge ?? 0), 0);

  return (
    <button
      className={`bn-section-tab${isActive ? ' bn-active' : ''}${isExpanded ? ' bn-expanded' : ''}${isFadedOut ? ' bn-faded' : ''}`}
      onClick={onTap}
      type="button"
      aria-label={t(section.titleKey)}
    >
      <div className="bn-icon-wrap">
        {Icon && <Icon size={24} />}
        {totalBadges > 0 && <span className="bn-badge-dot" />}
      </div>
      <span className="bn-tab-label">{t(section.titleKey)}</span>
    </button>
  );
}
